// src/components/hooks/useUnsavedChanges.ts

import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { registerNavigationGuard } from '@/lib/navigationGuard';

interface Options {
  /** Si hay cambios pendientes de guardar */
  when: boolean;
}

interface UnsavedChangesApi {
  /** true cuando hay que mostrar el diálogo de confirmación */
  promptOpen: boolean;
  /** Confirmar salida: navega/realiza la acción pendiente */
  confirmLeave: () => void;
  /** Cancelar salida: cierra el diálogo y permanece en la página */
  cancelLeave: () => void;
}

/**
 * Avisa al usuario de que tiene cambios sin guardar antes de salir,
 * usando un diálogo propio (el componente decide cómo renderizarlo)
 * en lugar del window.confirm nativo.
 *
 * Cubre:
 *  1. Cerrar / recargar pestaña → `beforeunload` (diálogo nativo del
 *     navegador; no es personalizable por seguridad).
 *  2. Navegación interna (clics en <Link>/<a>) → intercepta el clic,
 *     guarda el destino y abre el diálogo propio. Si se confirma,
 *     navega con react-router.
 *  3. Botón atrás/adelante → `popstate`: abre el diálogo y, si se
 *     confirma, retrocede de verdad.
 *  4. Navegación PROGRAMÁTICA disparada fuera de un click en <a>
 *     (ej. el tab bar inferior móvil, que llama a navigate() tras un
 *     gesto de arrastre) → vía navigationGuard, cualquier componente
 *     puede preguntar con guardNavigate(to) antes de navegar.
 *
 * Funciona con <BrowserRouter> clásico (v6).
 */
export function useUnsavedChanges({ when }: Options): UnsavedChangesApi {
  const navigate = useNavigate();

  const whenRef = useRef(when);
  whenRef.current = when;

  const [promptOpen, setPromptOpen] = useState(false);
  // Destino pendiente: una ruta (string) para navegación por clic,
  // o 'back' para el botón atrás del navegador.
  const pendingRef = useRef<string | 'back' | null>(null);

  // ── 1. Cerrar / recargar pestaña ─────────────────────────────
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!whenRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () =>
      window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // ── 2. Navegación interna (clics en enlaces) ─────────────────
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!whenRef.current) return;
      if (
        e.defaultPrevented ||
        e.button !== 0 ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey
      )
        return;

      const anchor = (e.target as HTMLElement)?.closest('a');
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      const target = anchor.getAttribute('target');

      if (
        !href ||
        href.startsWith('#') ||
        target === '_blank' ||
        anchor.hasAttribute('download') ||
        href.startsWith('mailto:') ||
        href.startsWith('tel:') ||
        href.startsWith('http')
      )
        return;

      if (href === window.location.pathname + window.location.search) return;

      // Interceptamos: guardamos destino y abrimos el diálogo propio
      e.preventDefault();
      e.stopPropagation();
      pendingRef.current = href;
      setPromptOpen(true);
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, []);

  // ── 3. Botón atrás / adelante del navegador ──────────────────
  useEffect(() => {
    if (!when) return;

    const pushGuard = () =>
      window.history.pushState(null, '', window.location.href);
    pushGuard();

    const handlePopState = () => {
      if (!whenRef.current) {
        return;
      }
      // Reponemos el estado para no salir todavía y abrimos el diálogo
      pushGuard();
      pendingRef.current = 'back';
      setPromptOpen(true);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [when]);

  // ── 4. Navegación programática externa (tab bar móvil, etc.) ─
  useEffect(() => {
    const unregister = registerNavigationGuard({
      isActive: () => whenRef.current,
      requestLeave: (to: string) => {
        pendingRef.current = to;
        setPromptOpen(true);
      },
    });
    return unregister;
  }, []);

  // ── Acciones del diálogo ─────────────────────────────────────
  const confirmLeave = useCallback(() => {
    const dest = pendingRef.current;
    pendingRef.current = null;
    setPromptOpen(false);
    // Desactivamos la guarda para que la navegación no se vuelva a interceptar
    whenRef.current = false;

    if (dest === 'back') {
      window.history.back();
    } else if (dest) {
      navigate(dest);
    }
  }, [navigate]);

  const cancelLeave = useCallback(() => {
    pendingRef.current = null;
    setPromptOpen(false);
  }, []);

  return { promptOpen, confirmLeave, cancelLeave };
}